import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ItemVisual } from '../components/ItemVisual';
import { Piko } from '../components/Piko';
import { audio } from '../core/audio';
import { recordSkill } from '../core/storage';

// Piko demonstrates a sequence; child repeats by tapping (Colour Trail / Copy Piko / Sound Sequence)
export function SequenceTap({ activity, onComplete }) {
  const [phase, setPhase] = useState('demo');
  const [demoIdx, setDemoIdx] = useState(-1);
  const [pos, setPos] = useState(0);
  const [wrong, setWrong] = useState(0);
  const stats = useRef({ attempts: 0, hints: 0 });
  const busy = useRef(false);

  const runDemo = async () => {
    setPhase('demo');
    setPos(0);
    await audio.playVoice(activity.voice);
    for (let i = 0; i < activity.sequence.length; i++) {
      setDemoIdx(i);
      if (activity.sounds) await audio.playObjectSound(activity.sounds[activity.sequence[i]]);
      else { audio.playSfx('tap'); await new Promise((r) => setTimeout(r, 800)); }
    }
    setDemoIdx(-1);
    setPhase('play');
  };

  useEffect(() => { const t = setTimeout(runDemo, 400); return () => clearTimeout(t); }, [activity.id]); // eslint-disable-line

  const tap = async (id) => {
    if (phase !== 'play' || busy.current) return;
    if (id === activity.sequence[pos]) {
      audio.playSfx('tap');
      if (activity.sounds) audio.playObjectSound(activity.sounds[id]);
      const np = pos + 1;
      if (np >= activity.sequence.length) {
        busy.current = true;
        stats.current.attempts += 1;
        recordSkill(activity.skill, true);
        audio.playSfx('correct');
        await audio.playVoice('correct2');
        onComplete({ correct: true, attempts: stats.current.attempts, hints: stats.current.hints });
      } else setPos(np);
    } else {
      busy.current = true;
      stats.current.attempts += 1;
      setWrong((w) => w + 1);
      recordSkill(activity.skill, false);
      audio.playSfx('retry');
      await audio.playVoice(wrong >= 1 ? 'hint' : 'retry1');
      if (wrong >= 1) stats.current.hints += 1;
      busy.current = false;
      runDemo();
    }
  };

  const hintNext = wrong >= 2 && phase === 'play';

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-8 px-5" data-testid="activity-sequence-tap">
      <div className="flex items-center gap-3">
        <Piko pose={phase === 'demo' ? 'point' : 'happy'} size={86} float={false} />
        <div className="flex gap-2">
          {activity.sequence.map((_, i) => (
            <div key={i} className={`w-4 h-4 rounded-full ${i < pos && phase === 'play' ? 'bg-leaf' : (demoIdx === i ? 'bg-sunshine' : 'bg-white/60')}`} />
          ))}
        </div>
      </div>
      <div className="flex gap-5 flex-wrap justify-center">
        {activity.choices.map((ch, i) => {
          const active = phase === 'demo' && activity.sequence[demoIdx] === ch.id;
          const hinted = hintNext && activity.sequence[pos] === ch.id;
          return (
            <motion.button key={`${ch.id}-${i}`} data-testid={`seq-choice-${ch.id}`}
              whileTap={phase === 'play' ? { scale: 0.92 } : {}}
              animate={active ? { scale: [1, 1.18, 1] } : hinted ? { scale: [1, 1.07, 1] } : {}}
              transition={hinted ? { repeat: Infinity, duration: 1.2 } : { duration: 0.5 }}
              onClick={() => tap(ch.id)}
              className="rounded-[2rem] p-3 bg-white/75 backdrop-blur min-w-[96px] min-h-[96px] flex items-center justify-center"
              style={{ border: '5px solid rgba(255,255,255,0.9)',
                boxShadow: active ? '0 0 0 6px #FFD166' : hinted ? '0 0 0 5px #FFD16690' : '0 10px 24px rgba(29,53,87,0.15)' }}>
              <ItemVisual item={ch} size={92} />
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
